import React from 'react';
import styled, { css } from 'styled-components';
import { useRef } from 'react';
import { useLocale } from '@react-aria/i18n';
import { useCalendarCell } from '@react-aria/calendar';
import { isSameDay, getDayOfWeek, isSameMonth } from '@internationalized/date';
import { useFocusRing } from '@react-aria/focus';
import { mergeProps } from '@react-aria/utils';

const CellWrapper = styled.div<{ isFocusVisible: boolean }>`
    position: relative;
    z-index: ${({ isFocusVisible }) => (isFocusVisible ? 10 : 0)};
`;

const CellButton = styled.div<{
    isRoundedLeft: boolean;
    isRoundedRight: boolean;
    isSelected: boolean;
    isDisabled: boolean;
}>`
    height: 2.5rem;
    outline: none;
    display: flex;
    align-items: center;
    justify-content: center;

    background-color: ${({ isSelected }) =>
        isSelected ? '#ebf4f8' : 'transparent'};
    color: ${({ isDisabled }) => (isDisabled ? '#9ca3af' : 'inherit')};
    cursor: ${({ isDisabled }) => (isDisabled ? 'not-allowed' : 'default')};

    ${({ isSelected }) =>
        isSelected &&
        css`
            &:hover {
                background-color: #a78bfa;
            }
        `}

    &:hover {
        background-color: ${({ isSelected, isDisabled }) =>
        !isSelected && !isDisabled
            ? '#e9d5ff'
            : 'inherit'};
    }
`;

const CellContent = styled.div<{
    isFocusVisible: boolean;
    isSelectionStart: boolean;
    isSelectionEnd: boolean;
}>`
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;

    ${({ isFocusVisible }) =>
        isFocusVisible &&
        css`
            box-shadow: 0 0 0 2px #006dcc;
            z-index: 2;
        `}

    ${({ isSelectionStart, isSelectionEnd }) =>
        (isSelectionStart || isSelectionEnd) &&
        css`
            background-color: #006dcc;
            color: white;
            &:hover {
                background-color: #6d28d9;
            }
        `}
`;

export const CalendarCell = ({ state, date, currentMonth }) => {
    const ref = useRef<HTMLDivElement | null>(null);
    const props = useCalendarCell({ date }, state, ref);
    const { cellProps, buttonProps, isSelected, isDisabled, formattedDate } =
        props;

    const isOutsideMonth = !isSameMonth(currentMonth, date);
    const isSelectionStart = state.highlightedRange
        ? isSameDay(date, state.highlightedRange.start)
        : isSelected;
    const isSelectionEnd = state.highlightedRange
        ? isSameDay(date, state.highlightedRange.end)
        : isSelected;

    const { locale } = useLocale();
    const dayOfWeek = getDayOfWeek(date, locale);
    const isRoundedLeft =
        isSelected && (isSelectionStart || dayOfWeek === 0 || date.day === 1);
    const isRoundedRight =
        isSelected &&
        (isSelectionEnd ||
            dayOfWeek === 6 ||
            date.day === date.calendar.getDaysInMonth(date));

    const { focusProps, isFocusVisible } = useFocusRing();

    return (
        <CellWrapper {...cellProps} isFocusVisible={isFocusVisible}>
            <CellButton
                {...mergeProps(buttonProps, focusProps)}
                ref={ref}
                isRoundedLeft={isRoundedLeft}
                isRoundedRight={isRoundedRight}
                isSelected={isSelected}
                isDisabled={isDisabled}
                hidden={isOutsideMonth}
            >
                <CellContent
                    isFocusVisible={isFocusVisible}
                    isSelectionStart={isSelectionStart}
                    isSelectionEnd={isSelectionEnd}
                >
                    {formattedDate}
                </CellContent>
            </CellButton>
        </CellWrapper>
    );
};
